import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Loader2, Plus, RefreshCw, Search } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import type { Job } from "./api"
import { useProducao } from "./context"
import { ContractNotices, Initials, MaterialBadge, Pill, TrelloSyncPill, VanPill } from "./components"
import { useMaterial } from "./material"
import { NewJobDialog } from "./NewJobDialog"
import { selectClass } from "./AssignPanel"
import {
  addDays,
  contractColumn,
  contractDateDrift,
  fmtShort,
  hasFieldStop,
  holdReasonOf,
  isHeld,
  hasAbsentTech,
  isAbsent,
  jobDays,
  KIND_SHORT,
  kindText,
  mondayOf,
  teamOf,
  todayIso,
  vanOf,
} from "./lib"

type ColumnDef = { key: string; title: string; sub: string; match(j: Job): boolean; droppable?: boolean }

const COLUMNS: ColumnDef[] = [
  { key: "por_atribuir", title: "Por atribuir", sub: "Adjudicadas sem data", match: (j) => j.status === "por_atribuir" },
  { key: "planeada", title: "Planeadas", sub: "Com data, carrinha e equipa", match: (j) => j.status === "planeada", droppable: true },
  { key: "execucao", title: "Em execução", sub: "A equipa começou", match: (j) => j.status === "execucao" },
  { key: "parada", title: "Paradas", sub: "Suspensas, com motivo", match: (j) => j.status === "parada" },
  { key: "concluida", title: "Concluídas", sub: "Últimos 7 dias", match: (j) => j.status === "concluida" },
]

/** Modo `host`: as colunas são o estado geral do contrato do Koortex. */
const HOST_COLUMNS: ColumnDef[] = [
  { key: "sem_contrato", title: "Sem contrato", sub: "Obras por ligar a um contrato", match: (j) => contractColumn(j) === "sem_contrato" },
  { key: "por_adjudicar", title: "Por adjudicar", sub: "À espera do pagamento no Odoo", match: (j) => contractColumn(j) === "por_adjudicar" },
  { key: "por_planear", title: "Por planear", sub: "Dono, carrinha e data", match: (j) => contractColumn(j) === "por_planear", droppable: true },
  { key: "em_execucao", title: "Em execução", sub: "Instalação e aceitação", match: (j) => contractColumn(j) === "em_execucao" },
  { key: "por_auditar", title: "Por certificar", sub: "Certificação no escritório", match: (j) => contractColumn(j) === "por_auditar" },
  { key: "parada", title: "Paradas", sub: "Contrato parado, com motivo", match: (j) => contractColumn(j) === "parada" },
]

type WeekFilter = "todas" | "esta" | "proxima"

/** G1 · Quadro de obras. */
export function Quadro() {
  const { board, loading, error, openAssign, syncPending, syncing, reload, refreshContracts } = useProducao()
  // Modo `host`: ao abrir o quadro, o estado (parada/cancelada) vem do contrato.
  useEffect(() => refreshContracts(), []) // eslint-disable-line react-hooks/exhaustive-deps
  const [newOpen, setNewOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [kind, setKind] = useState("")
  const [vanId, setVanId] = useState("")
  const [week, setWeek] = useState<WeekFilter>("todas")
  const [riskOnly, setRiskOnly] = useState(false)
  const [dragId, setDragId] = useState<string | null>(null)

  const today = todayIso()
  const monday = mondayOf(today)
  const sevenDaysAgo = addDays(today, -7)

  const hostMode = board?.config.contracts_backend === "host"
  const jobs = useMemo(() => {
    if (!board) return []
    return board.jobs.filter((j) => {
      if (j.status === "cancelada") return false
      // Modo `host`: concluídas e canceladas no contrato saem do quadro.
      if (hostMode) return contractColumn(j) != null
      if (j.status === "concluida" && (j.completed_at ?? j.updated_at).slice(0, 10) < sevenDaysAgo) return false
      return true
    })
  }, [board, sevenDaysAgo, hostMode])

  const inWeek = (j: Job, start: string) => {
    const days = jobDays(j)
    return days.some((d) => d >= start && d <= addDays(start, 5))
  }

  const filtered = jobs.filter((j) => {
    if (kind && j.kind !== kind) return false
    if (vanId && (vanId === "_none" ? j.van_id : j.van_id !== vanId)) return false
    if (week === "esta" && !inWeek(j, monday)) return false
    if (week === "proxima" && !inWeek(j, addDays(monday, 7))) return false
    const q = search.trim().toLowerCase()
    if (q && !`${j.client_name} ${j.city ?? ""} ${j.odoo_sale_order_name ?? ""}`.toLowerCase().includes(q)) return false
    return true
  })

  if (loading) return <Centered><Loader2 className="h-6 w-6 animate-spin" /></Centered>
  if (error || !board) return <Centered><p className="text-sm text-destructive">{error ?? "Sem dados."}</p></Centered>

  const porAtribuir = jobs.filter((j) => j.status === "por_atribuir").length
  const columns = hostMode ? HOST_COLUMNS.filter((c) => c.key !== "sem_contrato" || jobs.some(c.match)) : COLUMNS
  const estaSemana = jobs.filter((j) => j.status !== "por_atribuir" && j.status !== "concluida" && inWeek(j, monday)).length

  return (
    <div className="space-y-4">
      {/* Três perguntas: o que está por atribuir, quem vai a quê esta semana, o que está em risco. */}
      <div className="flex flex-wrap items-stretch gap-2 sm:gap-3">
        <Counter n={porAtribuir} label="Por atribuir" />
        <Counter n={estaSemana} label="Esta semana" />
        <RiskCounter jobs={jobs} active={riskOnly} onClick={() => setRiskOnly((v) => !v)} />
        <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
          {board.pending_sync > 0 && (
            <Button variant="outline" size="sm" onClick={() => void syncPending()} disabled={syncing}>
              <RefreshCw className={cn("h-4 w-4", syncing && "animate-spin")} />
              <span className="sm:hidden">Sincronizar ({board.pending_sync})</span>
              <span className="hidden sm:inline">Sincronizar pendentes ({board.pending_sync})</span>
            </Button>
          )}
          <Button size="sm" onClick={() => setNewOpen(true)}>
            <Plus className="h-4 w-4" />
            <span className="sm:hidden">Nova obra</span>
            <span className="hidden sm:inline">Obra a partir de ordem de venda</span>
          </Button>
        </div>
      </div>

      <ContractNotices board={board} />

      <div className="grid grid-cols-2 items-center gap-2 sm:flex sm:flex-wrap">
        <div className="relative col-span-2 w-full sm:w-64">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Pesquisar cliente" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Pesquisar cliente" />
        </div>
        <select className={cn(selectClass, "w-full min-w-0 sm:w-40")} value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Tipo">
          <option value="">Todos os tipos</option>
          {Object.entries(KIND_SHORT).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select className={cn(selectClass, "w-full min-w-0 sm:w-44")} value={vanId} onChange={(e) => setVanId(e.target.value)} aria-label="Carrinha">
          <option value="">Todas as carrinhas</option>
          {board.vans.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          <option value="_none">Sem carrinha</option>
        </select>
        <select className={cn(selectClass, "w-full min-w-0 sm:w-40")} value={week} onChange={(e) => setWeek(e.target.value as WeekFilter)} aria-label="Semana">
          <option value="todas">Todas as semanas</option>
          <option value="esta">Esta semana</option>
          <option value="proxima">Próxima semana</option>
        </select>
        <Button variant="ghost" size="sm" className="justify-self-start" onClick={() => void reload()}>
          <RefreshCw className="h-4 w-4" />
          Atualizar
        </Button>
        {riskOnly && <span className="col-span-2 text-xs font-medium text-red-700">A mostrar só as obras em risco</span>}
      </div>

      {/* Telemóvel e tablet: colunas lado a lado com deslize; a partir de 1280 px, as 5 cabem. */}
      <div
        className={cn(
          "-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 pb-2 sm:-mx-6 sm:scroll-px-6 sm:px-6 xl:mx-0 xl:grid xl:overflow-visible xl:px-0 xl:pb-0",
          columns.length > 5 ? "xl:grid-cols-6" : "xl:grid-cols-5",
        )}
      >
        {columns.map((col) => {
          const colJobs = filtered
            .filter(col.match)
            .sort((a, b) => (a.scheduled_date ?? "9999").localeCompare(b.scheduled_date ?? "9999") || a.client_name.localeCompare(b.client_name))
          const droppable = !!col.droppable
          return (
            <section
              key={col.key}
              className={cn(
                "flex min-h-[60vh] w-[82vw] min-w-0 max-w-[320px] shrink-0 snap-start flex-col rounded-xl bg-muted/50 p-2 sm:w-72 xl:w-auto xl:max-w-none",
                droppable && dragId && "ring-2 ring-accent/50",
              )}
              onDragOver={droppable ? (e) => e.preventDefault() : undefined}
              onDrop={
                droppable
                  ? () => {
                      // Arrastar para «Planeadas» abre sempre o painel de atribuição.
                      const j = board.jobs.find((x) => x.id === dragId)
                      if (j) openAssign({ job: j })
                      setDragId(null)
                    }
                  : undefined
              }
            >
              <header className="px-1.5 pb-2 pt-1">
                <div className="flex items-baseline justify-between">
                  <h2 className="text-sm font-semibold">{col.title}</h2>
                  <span className="text-xs text-muted-foreground">{colJobs.length}</span>
                </div>
                <p className="text-[11px] text-muted-foreground">{col.sub}</p>
              </header>
              <div className="flex flex-col gap-2">
                {colJobs.map((j) => (
                  <JobCard key={j.id} job={j} riskOnly={riskOnly} onDragStart={() => setDragId(j.id)} onDragEnd={() => setDragId(null)} />
                ))}
                {colJobs.length === 0 && <p className="px-1.5 py-4 text-center text-xs text-muted-foreground">Nada aqui</p>}
              </div>
            </section>
          )
        })}
      </div>

      <NewJobDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  )
}

/** Em risco: sem material (faltam), parada, ou técnico ausente num dia da obra. */
function useIsAtRisk(job: Job): boolean {
  const { board } = useProducao()
  const mat = useMaterial(job.id, job.odoo_sale_order_id != null && job.status !== "concluida")
  if (job.status === "concluida" || job.status === "cancelada") return false
  if (isHeld(job, board?.config.contracts_backend === "host")) return true
  if (mat?.status === "faltam" && job.status !== "por_atribuir") return true
  return hasAbsentTech(job, board?.absences ?? [])
}

function RiskCounter({ jobs, active, onClick }: { jobs: Job[]; active: boolean; onClick(): void }) {
  // Cada obra reporta o seu risco (o material chega aos poucos).
  const [risky, setRisky] = useState<Record<string, boolean>>({})
  const n = jobs.filter((j) => risky[j.id]).length
  return (
    <>
      {jobs.map((j) => (
        <RiskProbe key={j.id} job={j} onChange={(v) => setRisky((r) => (r[j.id] === v ? r : { ...r, [j.id]: v }))} />
      ))}
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={cn(
          "flex min-w-[104px] flex-1 flex-col rounded-xl border px-3 py-2 text-left transition-colors border-border sm:min-w-[140px] sm:flex-none sm:px-4",
          active ? "border-red-300 bg-red-50" : "hover:bg-muted/50",
        )}
      >
        <span className={cn("text-2xl font-semibold", n > 0 && "text-red-700")}>{n}</span>
        <span className="text-xs text-muted-foreground">Em risco {active ? "· a filtrar" : "· filtrar"}</span>
      </button>
    </>
  )
}

function RiskProbe({ job, onChange }: { job: Job; onChange(v: boolean): void }) {
  const risky = useIsAtRisk(job)
  useEffect(() => onChange(risky), [risky]) // eslint-disable-line react-hooks/exhaustive-deps
  return null
}

function Counter({ n, label }: { n: number; label: string }) {
  return (
    <div className="flex min-w-[104px] flex-1 flex-col rounded-xl border px-3 py-2 border-border sm:min-w-[140px] sm:flex-none sm:px-4">
      <span className="text-2xl font-semibold">{n}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  )
}

function JobCard({
  job,
  riskOnly,
  onDragStart,
  onDragEnd,
}: {
  job: Job
  riskOnly: boolean
  onDragStart(): void
  onDragEnd(): void
}) {
  const { board, openAssign } = useProducao()
  const navigate = useNavigate()
  const risky = useIsAtRisk(job)
  if (riskOnly && !risky) return null
  const technicians = board?.technicians ?? []
  const team = teamOf(job, technicians)
  const days = jobDays(job)
  const absentIds = team
    .filter((t) => days.some((d) => isAbsent(t.id, d, board?.absences ?? [])))
    .map((t) => t.id)
  const draggable = job.status === "por_atribuir"
  const hostMode = board?.config.contracts_backend === "host"
  // Data do contrato mudada no Koortex: o motor (telemóvel, conflitos) segue a da app — reatribuir.
  const drift = hostMode ? contractDateDrift(job) : null

  return (
    <article
      draggable={draggable}
      onDragStart={draggable ? onDragStart : undefined}
      onDragEnd={draggable ? onDragEnd : undefined}
      className={cn(
        "group rounded-lg border bg-card p-2.5 shadow-sm transition-shadow hover:shadow border-border",
        draggable && "cursor-grab active:cursor-grabbing",
        risky && "border-red-200",
      )}
    >
      <button type="button" className="block w-full text-left" onClick={() => navigate(`/producao/obras/${job.id}`)}>
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-2 text-sm font-semibold leading-snug">{job.client_name}</h3>
          {job.scheduled_date && (
            <span className="shrink-0 text-xs font-medium text-muted-foreground">
              {fmtShort(job.scheduled_date)}
              {job.duration_days > 1 && ` · ${job.duration_days}d`}
            </span>
          )}
        </div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {[job.city, kindText(job)].filter(Boolean).join(" · ")}
        </p>
        {isHeld(job, hostMode) && (hostMode || job.stop_reason) && (
          <p className="mt-1 text-xs text-red-700">{hostMode ? "Parada pelo gestor" : "Parada"}: {holdReasonOf(job, hostMode)}</p>
        )}
      </button>
      {hasFieldStop(job) && (
        <div className="mt-1.5">
          <Pill tone="warning" className="whitespace-normal">Paragem comunicada pelo técnico: {job.field_stop_reason ?? "sem motivo"}</Pill>
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {hostMode && job.contract?.phase_name && <Pill tone="info">{job.contract.phase_name}</Pill>}
        {hostMode && drift && <Pill tone="warning">Koortex: {fmtShort(drift)}</Pill>}
        <MaterialBadge job={job} compact />
        <TrelloSyncPill job={job} configured={board?.config.trello_configured ?? false} />
      </div>
      {(job.van_id || team.length > 0) && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <VanPill van={vanOf(job, board?.vans ?? [])} />
          <Initials techs={team} absentIds={absentIds} />
        </div>
      )}
      {job.status === "por_atribuir" && (
        <Button size="sm" variant="outline" className="mt-2 w-full" onClick={() => openAssign({ job })}>
          Atribuir
        </Button>
      )}
    </article>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex justify-center py-24 text-muted-foreground">{children}</div>
}
